import { initializeLuckyTurnipsState } from '../LuckyTurnips/LuckyTurnipsReducer';
import { LevelStatus } from '../Level/LevelStatus';
import { PlayerStateHelper } from '../Player/PlayerStateHelper';
import { IEndLevelData, IGameLoadData, ILoadWorldMetaData } from './GameActions';
import Game from '../../Game';
import { PreBoosterType } from '../Card/BoosterType';
import { IAPReward, RewardType } from '../../Utility/RewardHelper';
import UIStateHelper from '../UI/UIStateHelper';
import { AsyncActionStatus } from '../AsyncAction';
import { checkHelperFriendEnabled } from '../Level/HelperFriend/HelperFriendStateHelper';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { sendEvent, track } from '../../Utility/Tracking/Track';
import { LevelHelperSettings } from '../../Constants/Level';
import { MissionTrackingEventName, TrackingEventName } from '../../Utility/Tracking/TrackingEvent';
import { TActionType, TransactionType } from '../../Utility/Tracking/DataSet/Transaction';
import { trackResourceEvent } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { LevelType } from '../Player/Progress/PlayerLevels';
import { FTUXStateHelper } from './FTUX/FTUXStateHelper';
import { getLevelStarsTotal } from '../Player/PlayerLevelHelper';
import LevelStateHelper, { isDynamicAILevel } from '../Level/LevelStateHelper';
import { initialLevelState } from '../Level/initialLevelState';
import { GameScene } from './GameScene';
import { IGameState } from './IGameState';
import { initialUIState } from '../UI/IUIState';
import { report } from '../../report';
import LevelHelper from '../../Utility/LevelHelper';
import { isNative, isShopSupported } from '../../Utility/Platform';
import { MyPetMiniGameSceneParams } from '../../Scene/MyPetMiniGameScene';
import { StarChestStateHelper } from '../Player/StarChests/StarChestStateHelper';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { PopupID } from '../UI/PopupID';
import { LoginStateHelper } from '../Player/Login/LoginStateHelper';
import { PetsStateHelper } from '../Player/Pets/PetsStateHelper';
import PayloadStateHelper from './Payload/PayloadStateHelper';
import { IAPStateHelper } from '../Player/IAP/IAPStateHelper';
import { ConditionalTrackingHelper } from 'Utility/Tracking/TrackingHelpers/ConditionalTracking';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';
import { scheduleInactivityNotification } from 'State/Game/Action/ScheduleNotification';
import { ISceneParams } from 'Scene/Scene';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { QuestStateHelper } from "State/Player/Quests/QuestStateHelper";

export interface ILoadLevelMetaData {
    levelNumber: number;
    levelCost?: number;
    preBoosters: PreBoosterType[];
    restart?: boolean;
    attempts?: number;
    levelType?: LevelType;
    isFreePlay?: boolean;
    isLevelTest?: boolean;
}

export default class GameStateHelper {
    public static loadWorld(state: IGameState, params?: ILoadWorldMetaData): IGameState {
        const highestLevelCompleted = Game.player.getHighestLevelCompleted(state, LevelType.Core);

        // Unlock next world if not already unlocked
        if (ProgressStateHelper.canUnlockNextWorld(state, highestLevelCompleted)) {
            state = {
                ...state,
                playerState: ProgressStateHelper.unlockNextWorld(state.playerState),
            };
        }

        // Set star chest level if needed
        // FIXME: loading a world shouldn't be a requirement to spawn a star chest.
        //        move this logic to where user reaches new level
        const levelToSpawnStarChestOn = StarChestStateHelper.getLevelToSpawnStarChestOn(state);
        if (levelToSpawnStarChestOn !== null) {
            state = {
                ...state,
                playerState: StarChestStateHelper.setStarChestLevel(state.playerState, levelToSpawnStarChestOn),
            };
        }

        params = params || {};
        const world = params.worldNumber ?? state.playerState.maxWorld;
        params.clearUI = params.clearUI ?? true;
        params.firstComplete = Boolean(params.firstComplete);
        params.firstLoad = Boolean(params.firstLoad);

        let UIState = params.clearUI ? initialUIState : state.UIState;

        if (state.UIState.queue.length > 0) {
            UIState = UIStateHelper.nextPopup(state.UIState);
        }

        // Reset level state before checking for FTUX.
        state = {
            ...state,
            levelState : initialLevelState
        }

        state = PlayerStateHelper.queueFTUXIfNeeded(state);

        state = QuestStateHelper.fillQuestsIfNeeded(state);

        return {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.WORLD,
            },
            worldState: {
                worldNumber: world,
                firstComplete: params.firstComplete,
                firstLoad: params.firstLoad,
                starsGained: params.starsGained,
                petSpawnLevel: params.petSpawnLevel,
            },
            UIState: UIState,
        };
    }

    public static loadLevel(state: IGameState, params: ILoadLevelMetaData): IGameState {
        //Defaults
        params.attempts = params.attempts !== undefined ? params.attempts : 1;
        params.levelType = params.levelType !== undefined ? params.levelType : LevelType.Core;

        // Get level data
        let levelData = ProgressStateHelper.getLevelData(state, params.levelNumber);

        // If no level data then create a new entry
        if (!levelData) {
            levelData = {
                levelNumber: params.levelNumber,
                stars: null,
                helperFriendAttempts: 0,
                consecutiveUnsuccessfullAttempts: 0,
            };
        }

        // Set state
        state = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.LEVEL,
            },
            levelState: {
                ...initialLevelState,
                levelNumber: params.levelNumber,
                levelCost: params.levelCost,
                levelType: params.levelType,
                attempts: params.attempts,
                score: 0,
                status: LevelStatus.LOADING,
                // Reset booster prices at start of level
                boosterUses: undefined,
                boosterPrices: undefined,
                isFreePlay: params.isFreePlay,
                isLevelTest: params.isLevelTest ?? false,
                preBoosters: params.preBoosters,
            },
            UIState: initialUIState, // Reset UI state.
        };

        state = LevelStateHelper.getBoosterPrices(state);

        // Update the number of attempts since the level has been completed and since the helper was used
        state = ProgressStateHelper.updateLevel(state, params.levelNumber, {
            consecutiveUnsuccessfullAttempts: (levelData.consecutiveUnsuccessfullAttempts || 0) + 1,
            helperFriendAttempts: (levelData.helperFriendAttempts || 0) + 1,
        });

        if (isDynamicAILevel(state)) {
            levelData = ProgressStateHelper.getLevelData(state, params.levelNumber);

            if (levelData.consecutiveUnsuccessfullAttempts > LevelHelperSettings.minAttemptsToReduceDifficulty) {
                const difficultyReduction = state.playerState.progress.difficultyReduction + 1;
                state = PlayerStateHelper.setDifficultyReduction(state, difficultyReduction);
            }
        }

        // Check if friend helper should be activated this level
        state = checkHelperFriendEnabled(state);

        // Tracking
        track('mLevel', params.levelNumber);
        track('missionID', `level_saga_${params.levelNumber}`);
        track('missionName', `Level ${params.levelNumber}`);
        track('mLevelType', 'ALL_CARDS');
        track('mRetryNum', params.attempts - 1);
        track('lastLevelAttempted', params.levelNumber);
        track('tCoinsLevelEntry', params.levelCost);
        track('tActionType', TActionType.LEVEL_ENTRY);
        track('mEntryCoinCost', params.levelCost);
        track('mEntryWithBooster', params.isFreePlay ? 1 : 0);

        // Subtract coins in the levelSystem after the level has loaded, to ensure the players only spend coins on levels they get to play

        return state;
    }

    public static loadLuckyTurnips(state: IGameState): IGameState {
        const newState: IGameState = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.LUCKY_TURNIPS,
            },
            playerState: {
                ...state.playerState,
                luckyTurnipsState: initializeLuckyTurnipsState(
                    StarChestStateHelper.getStarChestUnlockTier(state.playerState),
                    state.playerState.luckyTurnipsState
                ),
            },
        };
        return newState;
    }

    public static loadLevelOrShowNoCoinsPopup(state: IGameState, params: ILoadLevelMetaData): IGameState {
        const { levelNumber, restart = false } = params;
        const starCount = ProgressStateHelper.getLevelStars(state, levelNumber);
        let levelCost: number = LevelHelper.getLevelEntryCost(levelNumber, starCount > 0);

        const isFreePlay: boolean = levelCost > 0 && state.playerState.inventory[RewardType.FREE_PLAY] > 0;

        if (isFreePlay) {
            levelCost = 0;
        }

        if (state.playerState.inventory.coins >= levelCost) {
            return GameStateHelper.loadLevel(state, {
                levelNumber,
                levelCost,
                isFreePlay,
                isLevelTest: params.isLevelTest,
                preBoosters: params.preBoosters,
                ...(restart ? { attempts: state.levelState.attempts + 1 } : {}),
            });
        } else {
            if (restart) {
                //:TODO SOMETHING IS BROKEN HERE...
                state = GameStateHelper.loadWorld(state, { worldNumber: state.worldState.worldNumber });
            }

            return {
                ...state,
                UIState: UIStateHelper.showPopup(
                    state.UIState,
                    isShopSupported(state) ? PopupID.SHOP_MENU : PopupID.NO_COINS_MENU,
                    {}
                ),
            };
        }
    }

    public static loadMyPetMiniGame(state: IGameState, params: MyPetMiniGameSceneParams): IGameState {
        const newState: IGameState = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.MY_PET_MINI_GAME,
                params,
            },
        };
        return newState;
    }

    public static loadFarmScene(state: IGameState, params: ISceneParams): IGameState {
        // Validate farm scene is not already loaded or currently loading.
        if (state.activeScene.result === GameScene.FARM) return state;

        state = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.FARM,
                params,
            },
            playerState: {
                ...FarmStateHelper.queueFarmFTUXIfNeeded(state.playerState)
            }
        };

        if (!FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM_BTN)) {
            state = {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXType.FARM_BTN])
            };
        }

        // Reset preview slot states to prevent showing confirm-animations when loading the scene.
        state = FarmStateHelper.clearFarmSlotPreviews(state);

        return state;
    }

    public static endLevel(state: IGameState, payload: IEndLevelData) {
        // Check before sending the resource again, its already sent on level fail popup is shown
        if (payload.status !== LevelStatus.RESTARTING) {
            GameStateHelper.trackLevelEnd(state, payload.status === LevelStatus.COMPLETE);
        }
        state = FTUXStateHelper.setLevelTasks(state);

        const status: LevelStatus =
            payload.status === LevelStatus.COMPLETE || payload.status === LevelStatus.FAILED
                ? LevelStatus.EXITING
                : payload.status;

        return {
            ...state,
            levelState: {
                ...state.levelState,
                status: status,
                firstComplete: payload.firstComplete,
            },
        };
    }

    public static trackLevelEnd(state: IGameState, levelComplete: boolean): void {
        try {
            const stars: number = getLevelStarsTotal(state.playerState);
            track('stars', stars);
            track('mChestStarsGained', state.levelState.completeData?.starsGained ?? 0);
            track('mStars', state.levelState.completeData?.stars ?? 0);
            track('mScore', state.levelState.completeData?.score ?? 0);
            track('tCoinsLevelEnd', state.levelState.completeData?.coins ?? 0);
            track('mMovesMade', state.levelState.cardsState.turns);
            track('lastLevelWon', Game.player.getHighestLevelCompleted(state));
            track('mCardsLeftOnBoard', state.levelState.cardsState.inPlay.length);

            trackResourceEvent(state, {
                transactionType: TransactionType.LEVEL_END,
                tActionType: TActionType.LEVEL_END,
            });
            sendEvent(
                levelComplete ? MissionTrackingEventName.missionCompleted : MissionTrackingEventName.missionFailed,
                state
            );
        } catch (error) {
            // preventing error 2541750930 on Sentry
            // because if `cardsState` is undefined then usually we already sent it
            const logFunc = Boolean(state.levelState?.cardsState) ? report : console.log;
            logFunc('Failed to track:', { error, once: true, sampleRate: 0.1 });
        }
        track('tCoinsLevelEnd', 0);
        track('tCoinsReceived', 0);
    }

    public static gameLoaded(state: IGameState, params: IGameLoadData): IGameState {
        state = {
            ...state,
            userState: {
                ...state.userState,
                ...params.userState,
            },
            platform: {
                ...state.platform,
                ...params.platform,
                adsInterstitial: params.platform.adsInterstitial,
                adsRewarded: params.platform.adsRewarded,
                subscribeBot: params.platform.subscribeBot,
            },
            entryPoint: params.entryPoint,
            worldState: {
                ...state.worldState,
                worldNumber: params.playerState.maxWorld,
            },
            eventsState: params.eventsState
        };

        // Initial setting of player state.
        state.playerState = PlayerStateHelper.updatePlayerState(state, params.playerState);

        // Call incrementSessionCount before checkDailyLogin to ensure that the daily session count is properly reset if its a new day
        state = LoginStateHelper.incrementSessionCount(state);
        state = LoginStateHelper.setFirstLoadTimer(state);
        state = LoginStateHelper.checkDailyLogin(state);
        state = PlayerStateHelper.checkTimers(state);

        state = ConditionalTrackingHelper.trackConditionally(state, [
            FBTrackingEvent.SESSION_5_3DAYS,
            FBTrackingEvent.SESSION_8_5DAYS,
            FBTrackingEvent.SESSION_11_7DAYS,
            FBTrackingEvent.SESSION_2_1DAY,
            FBTrackingEvent.SESSION_2_2DAYS,
            FBTrackingEvent.SESSION_2_3DAYS,
            FBTrackingEvent.SESSION_3_1DAY,
            FBTrackingEvent.SESSION_3_2DAYS,
            FBTrackingEvent.SESSION_3_3DAYS,
            FBTrackingEvent.SESSION_4_1DAY,
            FBTrackingEvent.SESSION_4_2DAYS,
            FBTrackingEvent.SESSION_4_3DAYS,
            FBTrackingEvent.SESSION_5_1DAY,
            FBTrackingEvent.SESSION_5_2DAYS,
            FBTrackingEvent.SESSION_5_5DAYS,
            FBTrackingEvent.SESSION_5_7DAYS,
        ]);

        if (params.entryPoint.entryPoint) {
            state = PayloadStateHelper.handleEntryPointPayload(state, params.entryPoint.payload);
        }

        state = PlayerStateHelper.setIsNewUser(state);

        if (state.perSessionData.isNewUser) {
            state = PetsStateHelper.setIsPetInDaily(state, true);
            FBPixel.track('Pixel_SOFA_NewUser');
        }

        if (state.perSessionData.isNewUser || state.playerState.forceNewUserDdnaEvent) {
            sendEvent(TrackingEventName.newPlayer, state);
            state = PlayerStateHelper.setPlayerState(state, { ...state.playerState, forceNewUserDdnaEvent: true });
        }

        sendEvent(TrackingEventName.gameStarted, state);

        if(isNative()) {
            scheduleInactivityNotification(state);
        }

        return state;
    }

    public static enableIAP(state: IGameState, params: IAPReward[]): IGameState {
        if (params) {
            state = IAPStateHelper.handleUnconsumedPurchases(state, params);
        }

        return {
            ...state,
            platform: {
                ...state.platform,
                IAPSupport: true,
            },
        };
    }
}
